/* ProposalDocument — branded, print-friendly rendering of a proposal snapshot.
 * Shared by the public client page and the builder's PDF export so both look
 * identical. `snap` matches the server `sent_snapshot` shape. */
import { forwardRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (u) => (u && u.startsWith('/') ? `${BACKEND_URL}${u}` : u);
const money = (cents, currency = 'usd') => {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format((cents || 0) / 100); }
  catch { return `$${((cents || 0) / 100).toFixed(2)}`; }
};

export const ProposalDocument = forwardRef(({ snap }, ref) => {
  const brandColor = snap.branding?.color || '#4f46e5';
  const currency = snap.currency || 'usd';
  return (
    <div ref={ref} className="bg-white p-8" style={{ maxWidth: 720, margin: '0 auto' }} data-testid="proposal-document">
      <div className="flex items-center justify-between gap-3 pb-4" style={{ borderBottom: `3px solid ${brandColor}` }}>
        <div className="flex items-center gap-3">
          {snap.branding?.logo_url
            ? <img src={fullUrl(snap.branding.logo_url)} alt="logo" crossOrigin="anonymous" style={{ height: 44, width: 44, objectFit: 'contain' }} />
            : <div style={{ height: 44, width: 44, borderRadius: 8, background: brandColor }} />}
          <div>
            <p className="font-semibold text-zinc-900">{snap.branding?.company_name || 'Proposal'}</p>
            {snap.branding?.company_info && <p className="text-xs text-zinc-500">{snap.branding.company_info}</p>}
          </div>
        </div>
        <p className="text-xs text-zinc-400 font-mono">{snap.number}</p>
      </div>

      <h1 className="text-2xl font-bold text-zinc-900 mt-6">{snap.title}</h1>
      {snap.prospect?.name && <p className="text-sm text-zinc-500 mt-1">Prepared for {snap.prospect.name}{snap.prospect.company ? ` · ${snap.prospect.company}` : ''}</p>}

      {snap.intro && <p className="text-sm text-zinc-700 whitespace-pre-wrap mt-5">{snap.intro}</p>}
      {snap.scope && (<div className="mt-5"><h3 className="text-sm font-semibold text-zinc-900 mb-1">Scope of work</h3><p className="text-sm text-zinc-700 whitespace-pre-wrap">{snap.scope}</p></div>)}

      <div className="mt-6">
        <h3 className="text-sm font-semibold text-zinc-900 mb-2">Investment</h3>
        <div className="border border-zinc-200 rounded-lg overflow-hidden">
          {(snap.line_items || []).map((li) => {
            const net = Math.max(0, Math.round((li.qty || 0) * (li.unit_price_cents || 0)) - (li.discount_cents || 0));
            const lineTotal = net + Math.round(net * (li.tax_pct || 0) / 100);
            return (
              <div key={li.id || li.name} className="flex items-start justify-between gap-3 px-3 py-2 border-b border-zinc-100 last:border-0">
                <div>
                  <p className="text-sm font-medium text-zinc-800">{li.name} {li.billing === 'monthly' && <span className="text-xs" style={{ color: brandColor }}>(monthly)</span>}</p>
                  {li.description && <p className="text-xs text-zinc-500">{li.description}</p>}
                  <p className="text-xs text-zinc-400">{li.qty} × {money(li.unit_price_cents, currency)}{li.discount_cents ? ` − ${money(li.discount_cents, currency)}` : ''}</p>
                </div>
                <p className="text-sm font-semibold whitespace-nowrap">{money(lineTotal, currency)}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-3 space-y-1 text-sm">
          <div className="flex justify-between font-semibold"><span>One-time total</span><span>{money(snap.totals?.one_time_total_cents, currency)}</span></div>
          {snap.totals?.monthly_total_cents > 0 && <div className="flex justify-between font-semibold" style={{ color: brandColor }}><span>Monthly</span><span>{money(snap.totals.monthly_total_cents, currency)}/mo</span></div>}
          {snap.payment_option === 'deposit' && snap.deposit_pct && <p className="text-xs text-zinc-500">Deposit of {snap.deposit_pct}% due on acceptance.</p>}
        </div>
      </div>

      {snap.terms && (<div className="mt-6"><h3 className="text-sm font-semibold text-zinc-900 mb-1">Terms</h3><p className="text-xs text-zinc-600 whitespace-pre-wrap">{snap.terms}</p></div>)}
      <p className="text-center text-[10px] text-zinc-400 mt-8">Powered by PostToSocial</p>
    </div>
  );
});

ProposalDocument.displayName = 'ProposalDocument';
export default ProposalDocument;
